import React, { useState, useMemo } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  DatePicker,
  Descriptions,
  Progress,
  Upload,
  Alert,
  Checkbox,
} from 'antd';
import type { UploadProps } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ToolOutlined, SettingOutlined, WarningOutlined, DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import {
  useWorkstations,
  useCreateWorkstation,
  useUpdateWorkstation,
  useDeleteWorkstation,
  useDownloadWorkstationImportTemplate,
  useImportWorkstationsCsv,
} from '@/hooks/useManufacturing';
import { useDepartmentsForSelection } from '@/hooks/useDepartments';
import { useUsers } from '@/hooks/useQCWorkflow';
import type { Workstation, WorkstationImportSummary } from '@/services/manufacturing.service';

const WorkstationsPage: React.FC = () => {
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingWorkstation, setEditingWorkstation] = useState<Workstation | null>(null);

  // API hooks
  const { data: users = [] } = useUsers({ is_active: true });
  const { data: workstations = [], isLoading } = useWorkstations();
  const { data: departments = [] } = useDepartmentsForSelection();
  const createWorkstation = useCreateWorkstation();
  const updateWorkstation = useUpdateWorkstation();
  const deleteWorkstation = useDeleteWorkstation();
  const downloadTemplate = useDownloadWorkstationImportTemplate();
  const importCsv = useImportWorkstationsCsv();
  const [importSummary, setImportSummary] = useState<WorkstationImportSummary | null>(null);
  const [importResultModalOpen, setImportResultModalOpen] = useState(false);
  const [importUpdateExisting, setImportUpdateExisting] = useState(true);

  const workstationTypes = [
    'Machine',
    'Assembly Station',
    'Quality Check Station',
    'Packing Station',
    'Manual Workstation',
    'Automated Workstation',
  ];

  const statusOptions = [
    { value: 'Available', color: 'green' },
    { value: 'In Use', color: 'blue' },
    { value: 'Under Maintenance', color: 'orange' },
    { value: 'Breakdown', color: 'red' },
    { value: 'Idle', color: 'default' },
  ];

  const columns: ColumnsType<Workstation> = [
    {
      title: 'Code',
      dataIndex: 'workstation_code',
      key: 'workstation_code',
      width: 150,
      fixed: 'left',
    },
    {
      title: 'Name',
      dataIndex: 'workstation_name',
      key: 'workstation_name',
      width: 200,
      fixed: 'left',
    },
    {
      title: 'Type',
      dataIndex: 'workstation_type',
      key: 'workstation_type',
      width: 180,
      render: (type: string) => <Tag color="blue">{type}</Tag>,
    },
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
      width: 150,
    },
    {
      title: 'Department',
      key: 'department',
      width: 150,
      render: (_: unknown, r: Workstation) =>
        r.department_id
          ? (departments.find((d) => d.id === r.department_id)?.department_name ?? r.department ?? '-')
          : (r.department ?? '-'),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 150,
      render: (status: string) => {
        const statusConfig = statusOptions.find(s => s.value === status);
        return <Tag color={statusConfig?.color}>{status}</Tag>;
      },
    },
    {
      title: 'Capacity/Day',
      dataIndex: 'capacity_per_day',
      key: 'capacity_per_day',
      width: 120,
      align: 'center',
      render: (capacity: number) => `${capacity} hrs`,
    },
    {
      title: 'Efficiency',
      dataIndex: 'efficiency_percentage',
      key: 'efficiency_percentage',
      width: 120,
      align: 'center',
      render: (efficiency: number) => (
        <Progress
          type="circle"
          percent={efficiency}
          width={50}
          strokeColor={efficiency > 80 ? '#52c41a' : efficiency > 60 ? '#faad14' : '#f5222d'}
        />
      ),
    },
    {
      title: 'Operators',
      dataIndex: 'number_of_operators_required',
      key: 'number_of_operators_required',
      width: 100,
      align: 'center',
    },
    {
      title: 'Hourly Cost',
      dataIndex: 'hourly_cost',
      key: 'hourly_cost',
      width: 120,
      align: 'right',
      render: (cost?: number) => cost ? `$${cost}` : '-',
    },
    {
      title: 'Next Maintenance',
      dataIndex: 'next_maintenance_date',
      key: 'next_maintenance_date',
      width: 150,
      render: (date?: string) => {
        if (!date) return '-';
        const isOverdue = dayjs(date).isBefore(dayjs());
        return (
          <Space>
            {isOverdue && <WarningOutlined style={{ color: '#f5222d' }} />}
            <span style={{ color: isOverdue ? '#f5222d' : undefined }}>
              {dayjs(date).format('DD-MMM-YYYY')}
            </span>
          </Space>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 150,
      render: (_: any, record: Workstation) => (
        <Space size="small">
          <Button
            type="text"
            icon={<SettingOutlined />}
            size="small"
            onClick={() => handleView(record)}
            title="View Details"
          />
          <Button
            type="text"
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEdit(record)}
          />
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            size="small"
            onClick={() => record.id && handleDelete(record.id)}
          />
        </Space>
      ),
    },
  ];

  const handleView = (workstation: Workstation) => {
    Modal.info({
      title: workstation.workstation_name,
      width: 700,
      content: (
        <Descriptions column={2} bordered size="small" style={{ marginTop: 20 }}>
          <Descriptions.Item label="Code" span={2}>{workstation.workstation_code}</Descriptions.Item>
          <Descriptions.Item label="Type">{workstation.workstation_type}</Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag color={statusOptions.find(s => s.value === workstation.status)?.color}>
              {workstation.status}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Location">{workstation.location || '-'}</Descriptions.Item>
          <Descriptions.Item label="Department">
            {workstation.department_id
              ? (departments.find((d) => d.id === workstation.department_id)?.department_name ?? workstation.department ?? '-')
              : (workstation.department || '-')}
          </Descriptions.Item>
          <Descriptions.Item label="Capacity/Day">{workstation.capacity_per_day} hrs</Descriptions.Item>
          <Descriptions.Item label="Efficiency">{workstation.efficiency_percentage}%</Descriptions.Item>
          <Descriptions.Item label="Operators Required">{workstation.number_of_operators_required}</Descriptions.Item>
          <Descriptions.Item label="Default Operator">
            {(() => {
              const uid = workstation.default_operator_user_id;
              if (!uid) return '—';
              const u = (users as { id: string; first_name?: string; last_name?: string; email?: string }[]).find(
                (x) => x.id === uid
              );
              if (!u) return uid;
              const name = [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
              return name || u.email || uid;
            })()}
          </Descriptions.Item>
          <Descriptions.Item label="Hourly Cost">${workstation.hourly_cost || 0}</Descriptions.Item>
          <Descriptions.Item label="Setup Time">{workstation.setup_time_hours || 0} hrs</Descriptions.Item>
          <Descriptions.Item label="Maintenance Interval">{workstation.maintenance_interval_days} days</Descriptions.Item>
          <Descriptions.Item label="Last Maintenance">
            {workstation.last_maintenance_date ? dayjs(workstation.last_maintenance_date).format('DD-MMM-YYYY') : 'Never'}
          </Descriptions.Item>
          <Descriptions.Item label="Next Maintenance">
            {workstation.next_maintenance_date ? dayjs(workstation.next_maintenance_date).format('DD-MMM-YYYY') : 'Not scheduled'}
          </Descriptions.Item>
          {workstation.manufacturer && (
            <>
              <Descriptions.Item label="Manufacturer" span={2}>{workstation.manufacturer}</Descriptions.Item>
              <Descriptions.Item label="Model">{workstation.model}</Descriptions.Item>
              <Descriptions.Item label="Serial Number">{workstation.serial_number}</Descriptions.Item>
            </>
          )}
        </Descriptions>
      ),
    });
  };

  const handleEdit = (workstation: Workstation) => {
    setEditingWorkstation(workstation);
    form.setFieldsValue({
      ...workstation,
      department_id: workstation.department_id ?? undefined,
      last_maintenance_date: workstation.last_maintenance_date ? dayjs(workstation.last_maintenance_date) : null,
      next_maintenance_date: workstation.next_maintenance_date ? dayjs(workstation.next_maintenance_date) : null,
    });
    setIsModalVisible(true);
  };

  const handleDelete = (id: string) => {
    Modal.confirm({
      title: 'Delete Workstation',
      content: 'Are you sure you want to delete this workstation?',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk: () => {
        deleteWorkstation.mutate(id);
      },
    });
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();

      const workstationData: any = {
        ...values,
        last_maintenance_date: values.last_maintenance_date?.format('YYYY-MM-DD'),
        next_maintenance_date: values.next_maintenance_date?.format('YYYY-MM-DD'),
      };

      if (editingWorkstation) {
        await updateWorkstation.mutateAsync({ id: editingWorkstation.id!, data: workstationData });
      } else {
        await createWorkstation.mutateAsync(workstationData);
      }

      setIsModalVisible(false);
      form.resetFields();
      setEditingWorkstation(null);
    } catch (error) {
      console.error('Operation failed:', error);
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
    setEditingWorkstation(null);
  };

  const handleAdd = () => {
    setEditingWorkstation(null);
    form.resetFields();
    form.setFieldsValue({ 
      efficiency_percentage: 100, 
      number_of_operators_required: 1,
      capacity_per_day: 8,
      maintenance_interval_days: 90,
      is_active: true,
      status: 'Available'
    });
    setIsModalVisible(true);
  };

  const importUploadProps: UploadProps = useMemo(
    () => ({
      accept: '.csv',
      showUploadList: false,
      beforeUpload: (file) => {
        importCsv.mutate(
          { file: file as File, updateExisting: importUpdateExisting },
          {
            onSuccess: (summary) => {
              setImportSummary(summary);
              setImportResultModalOpen(true);
            },
          }
        );
        return false;
      },
    }),
    [importCsv, importUpdateExisting]
  );

  const importDetailColumns: ColumnsType<WorkstationImportSummary['details'][0]> = [
    { title: 'Row', dataIndex: 'row', width: 70 },
    { title: 'Code', dataIndex: 'workstation_code', width: 140, ellipsis: true },
    {
      title: 'Outcome',
      dataIndex: 'outcome',
      width: 100,
      render: (o: string) => (
        <Tag
          color={
            o === 'created' ? 'green' : o === 'updated' ? 'blue' : o === 'skipped' ? 'default' : 'red'
          }
        >
          {o}
        </Tag>
      ),
    },
    { title: 'Message', dataIndex: 'message', ellipsis: true },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card
        title={
          <Space>
            <ToolOutlined />
            <span>Workstation Management</span>
          </Space>
        }
        extra={
          <Space wrap>
            <Button
              icon={<DownloadOutlined />}
              onClick={() => downloadTemplate.mutate()}
              loading={downloadTemplate.isPending}
            >
              Download import template
            </Button>
            <Upload {...importUploadProps}>
              <Button icon={<UploadOutlined />} loading={importCsv.isPending}>
                Import CSV
              </Button>
            </Upload>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              New Workstation
            </Button>
          </Space>
        }
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Bulk import"
          description={
            <>
              Download the CSV template, fill in your workstations (required columns: workstation_code,
              workstation_name, workstation_type, status), then use Import CSV.{' '}
              <strong>workstation_type</strong> and <strong>status</strong> must match the exact values used in the
              form (e.g. Machine, Available). Use <strong>department_code</strong> to link a finance department by
              code, or leave blank. By default, rows with an existing <strong>workstation_code</strong> are{' '}
              <strong>updated</strong> from the CSV; turn off the checkbox below to skip duplicates instead.
            </>
          }
        />
        <div style={{ marginBottom: 12 }}>
          <Checkbox checked={importUpdateExisting} onChange={(e) => setImportUpdateExisting(e.target.checked)}>
            Update existing workstations when workstation code already exists (recommended for re-imports)
          </Checkbox>
        </div>
        <Table
          columns={columns}
          dataSource={workstations}
          rowKey="id"
          loading={isLoading}
          scroll={{ x: 1800 }}
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} workstations`,
          }}
        />
      </Card>

      <Modal
        title="Import results"
        open={importResultModalOpen}
        onCancel={() => {
          setImportResultModalOpen(false);
          setImportSummary(null);
        }}
        footer={[
          <Button
            key="close"
            type="primary"
            onClick={() => {
              setImportResultModalOpen(false);
              setImportSummary(null);
            }}
          >
            Close
          </Button>,
        ]}
        width={720}
      >
        {importSummary ? (
          <div>
            <Space style={{ marginBottom: 12 }} wrap>
              <Tag color="green">Created: {importSummary.created}</Tag>
              <Tag color="blue">Updated: {importSummary.updated ?? 0}</Tag>
              <Tag>Skipped: {importSummary.skipped}</Tag>
              <Tag color={importSummary.failed > 0 ? 'red' : 'default'}>Errors: {importSummary.failed}</Tag>
            </Space>
            {importSummary.details?.length ? (
              <Table
                size="small"
                rowKey={(r, i) => `${r.row}-${i}`}
                dataSource={importSummary.details}
                columns={importDetailColumns}
                pagination={{ pageSize: 10 }}
                scroll={{ y: 360 }}
              />
            ) : (
              <span>No per-row details returned.</span>
            )}
          </div>
        ) : null}
      </Modal>

      <Modal
        title={editingWorkstation ? 'Edit Workstation' : 'New Workstation'}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        confirmLoading={createWorkstation.isPending || updateWorkstation.isPending}
        width={900}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 20 }}>
          <Space size="large" style={{ width: '100%' }}>
            <Form.Item
              name="workstation_code"
              label="Workstation Code"
              rules={[{ required: true, message: 'Please enter code' }]}
              style={{ flex: 1 }}
            >
              <Input placeholder="WS-CNC-001" />
            </Form.Item>

            <Form.Item
              name="workstation_name"
              label="Workstation Name"
              rules={[{ required: true, message: 'Please enter name' }]}
              style={{ flex: 1 }}
            >
              <Input placeholder="CNC Machine 1" />
            </Form.Item>
          </Space>

          <Space size="large" style={{ width: '100%' }}>
            <Form.Item
              name="workstation_type"
              label="Type"
              rules={[{ required: true, message: 'Please select type' }]}
              style={{ flex: 1 }}
            >
              <Select placeholder="Select type">
                {workstationTypes.map(type => (
                  <Select.Option key={type} value={type}>{type}</Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="status"
              label="Status"
              rules={[{ required: true, message: 'Please select status' }]}
              style={{ flex: 1 }}
            >
              <Select placeholder="Select status">
                {statusOptions.map(status => (
                  <Select.Option key={status.value} value={status.value}>{status.value}</Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Space>

          <Space size="large" style={{ width: '100%' }}>
            <Form.Item name="location" label="Location" style={{ flex: 1 }}>
              <Input placeholder="Shop Floor A" />
            </Form.Item>

            <Form.Item name="department_id" label="Department" style={{ flex: 1 }}>
              <Select
                allowClear
                placeholder="Select department"
                options={departments.map((d) => ({ value: d.id, label: `${d.department_code} - ${d.department_name}` }))}
              />
            </Form.Item>
          </Space>

          <Space size="large" style={{ width: '100%' }}>
            <Form.Item
              name="capacity_per_day"
              label="Capacity/Day (hours)"
              rules={[{ required: true, message: 'Please enter capacity' }]}
            >
              <InputNumber min={0} max={24} step={0.5} style={{ width: 150 }} />
            </Form.Item>

            <Form.Item
              name="efficiency_percentage"
              label="Efficiency %"
              rules={[{ required: true, message: 'Please enter efficiency' }]}
            >
              <InputNumber min={0} max={100} style={{ width: 150 }} />
            </Form.Item>

            <Form.Item
              name="number_of_operators_required"
              label="Operators Required"
              rules={[{ required: true, message: 'Please enter operators' }]}
            >
              <InputNumber min={0} max={10} style={{ width: 150 }} />
            </Form.Item>

            <Form.Item name="default_operator_user_id" label="Operator">
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Select default operator"
                style={{ width: 280 }}
                options={(users as { id: string; first_name?: string; last_name?: string; email?: string }[]).map(
                  (u) => {
                    const name = [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
                    const label = name ? `${name}${u.email ? ` (${u.email})` : ''}` : u.email || u.id;
                    return { value: u.id, label };
                  }
                )}
              />
            </Form.Item>
          </Space>

          <Space size="large" style={{ width: '100%' }}>
            <Form.Item name="hourly_cost" label="Hourly Cost ($)">
              <InputNumber min={0} step={0.01} style={{ width: 150 }} />
            </Form.Item>

            <Form.Item name="setup_time_hours" label="Setup Time (hours)">
              <InputNumber min={0} step={0.25} style={{ width: 150 }} />
            </Form.Item>

            <Form.Item name="maintenance_interval_days" label="Maintenance Interval (days)">
              <InputNumber min={0} style={{ width: 150 }} />
            </Form.Item>
          </Space>

          <Space size="large" style={{ width: '100%' }}>
            <Form.Item name="last_maintenance_date" label="Last Maintenance">
              <DatePicker style={{ width: 200 }} />
            </Form.Item>

            <Form.Item name="next_maintenance_date" label="Next Maintenance">
              <DatePicker style={{ width: 200 }} />
            </Form.Item>
          </Space>

          <Space size="large" style={{ width: '100%' }}>
            <Form.Item name="manufacturer" label="Manufacturer" style={{ flex: 1 }}>
              <Input placeholder="Haas Automation" />
            </Form.Item>

            <Form.Item name="model" label="Model" style={{ flex: 1 }}>
              <Input placeholder="VF-2SS" />
            </Form.Item>

            <Form.Item name="serial_number" label="Serial Number" style={{ flex: 1 }}>
              <Input placeholder="HAS-2025-001" />
            </Form.Item>
          </Space>
        </Form>
      </Modal>
    </div>
  );
};

export default WorkstationsPage;

